/**
 * Signals Hub signal-type catalogue (mb-frontend#4465).
 *
 * One list drives the grouped signal-type filter, the row chips, the detail
 * panel event list and the chip tone. Types are grouped by what they MEAN to a
 * seller, not by the table they came from: observed activity carries recency,
 * relationship context is standing knowledge about a person, company events are
 * third-party facts about the account, and fit recommendations imply no intent.
 *
 * The keys are the normalized `type` values the unified signals endpoint emits in
 * `signal_summary[]` (mb-server `utils/signalTypeCatalog.js` mirrors this list).
 * Keep both in sync when adding a type.
 *
 * The file is also the frontend's one signal-type registry: every surface that
 * names a type (Tasks, the Tasks and Today chips, settings, `colors.signals`)
 * reads a view of `SIGNAL_TYPE_REGISTRY` instead of keeping its own table.
 *
 * Explicit work is deliberately absent: replies, follow-ups, manual tasks,
 * recordings, chatbot leads and form submissions are Actions, not signals to
 * review here.
 */

export type SignalMeaning = 'activity' | 'relationship' | 'company_event' | 'fit';

export interface SignalTypeDefinition {
  key: string;
  label: string;
  meaning: SignalMeaning;
  /** Hover copy where the label alone would overstate what the signal proves. */
  tooltip?: string;
}

export interface SignalMeaningGroup {
  meaning: SignalMeaning;
  title: string;
  note: string;
}

export const SIGNAL_MEANING_GROUPS: SignalMeaningGroup[] = [
  { meaning: 'activity', title: 'Observed activity', note: 'Something happened. Recency drives sort order.' },
  { meaning: 'relationship', title: 'Relationship context', note: 'Who we already know. Not activity.' },
  { meaning: 'company_event', title: 'Company events', note: 'Third-party events about the account.' },
  { meaning: 'fit', title: 'Fit recommendations', note: 'Fit only. No buying intent implied.' },
];

/**
 * Surfaces that still word a type their own way. Each is a view over the
 * registry below; an override records where a surface differs from `label`
 * (or, for the Tasks chips, from the surface it falls back to) so the wording
 * can be converged one surface at a time by deleting overrides.
 *
 * - `tasks`: `SIGNAL_TYPE_CONFIG` — Tasks detail panel, type filter, admin pages.
 * - `taskChip`: the Tasks list row chip (`getSignalStatusDisplay`); falls back to `tasks`.
 * - `todayChip`: the Today dashboard row chip (`todaySignalTypeChip`); falls back to
 *   `shortLabel`, then `taskChip`.
 * - `settings`: GTM signal settings (`GTM_SIGNAL_TYPE_DISPLAY_METADATA`).
 *
 * The Signals Hub has no override: `label` is its wording.
 */
export type SignalTypeSurface = 'tasks' | 'taskChip' | 'todayChip' | 'settings';

export interface SignalTypeEntry {
  key: string;
  /** Canonical wording, and the Signals Hub's. */
  label: string;
  /** Compact chip wording where a row has no room for `label`. */
  shortLabel?: string;
  /**
   * lucide icon name. Every type Tasks renders has one; a type without one is
   * not a Tasks type and is absent from `SIGNAL_TYPE_CONFIG`.
   */
  icon?: string;
  /** Hex colour for dots and chips. Absent → the neutral `colors.signals.default`. */
  color?: string;
  /** Present iff the type is in the Signals Hub catalogue. */
  hub?: { meaning: SignalMeaning; tooltip?: string };
  labels?: Partial<Record<SignalTypeSurface, string>>;
  /** Settings dot colour where it differs from `color` (compared case-insensitively). */
  settingsColor?: string;
}

/**
 * Every signal type the frontend names, in Tasks order (the order
 * `SIGNAL_TYPE_CONFIG` has always had), hub-only types last.
 */
export const SIGNAL_TYPE_REGISTRY = [
  {
    key: 'website_visit', label: 'Website visit', icon: 'Globe', color: '#3B82F6',
    hub: { meaning: 'activity' },
    labels: { tasks: 'Website Visitor', taskChip: 'Visited website', settings: 'Visited website' },
  },
  {
    key: 'vector_lead', label: 'Identified visitor', icon: 'Users', color: '#10B981',
    hub: { meaning: 'activity' },
    labels: { tasks: 'Person Visitor', settings: 'Person Visitor' },
  },
  {
    key: 'podcast_episode', label: 'Podcast mention', icon: 'Mic', color: '#A855F7',
    hub: { meaning: 'activity' },
    labels: { tasks: 'Podcast' },
    settingsColor: '#d946ef',
  },
  {
    key: 'market_intel', label: 'Market intel', icon: 'TrendingUp', color: '#F59E0B',
    hub: { meaning: 'company_event' },
    labels: { tasks: 'Market Intel', taskChip: 'Recently in the news', settings: 'Recently in the news' },
  },
  {
    key: 'regulatory_filing', label: 'Regulatory filing', icon: 'FileText', color: '#0EA5E9',
    hub: { meaning: 'company_event' },
    labels: { tasks: 'Regulatory Filing' },
  },
  {
    key: 'funding_round', label: 'Funding round', icon: 'DollarSign', color: '#10B981',
    hub: { meaning: 'company_event' },
    labels: { tasks: 'Funding Round' },
  },
  {
    key: 'competitor_event', label: 'Competitor event', icon: 'Swords', color: '#EF4444',
    hub: { meaning: 'company_event' },
    labels: { tasks: 'Competitor Event' },
  },
  {
    key: 'dev_intent', label: 'Dev intent', icon: 'Code', color: '#6366F1',
    hub: { meaning: 'company_event' },
    labels: { tasks: 'Dev Intent' },
  },
  {
    key: 'ai_discovery', label: 'AI discovery', icon: 'Sparkles', color: '#6366F1',
    hub: { meaning: 'fit', tooltip: 'Fit recommendation. No activity observed yet.' },
    labels: { tasks: 'AI Discovery' },
  },
  {
    key: 'job_posting', label: 'Hiring', icon: 'Briefcase', color: '#22C55E',
    hub: { meaning: 'company_event' },
    // Posting-based Hiring is opt-in since 2026-10-28; the New hire signal is the default.
    labels: { settings: 'Company is hiring (job postings, opt-in)' },
    settingsColor: '#6366f1',
  },
  {
    key: 'email_reply', label: 'Email reply', shortLabel: 'Reply', icon: 'Mail', color: '#F59E0B',
    labels: { tasks: 'Email Reply' },
    settingsColor: '#06b6d4',
  },
  {
    key: 'linkedin_reply', label: 'LinkedIn reply', shortLabel: 'Reply', icon: 'Linkedin', color: '#0A66C2',
    labels: { tasks: 'LinkedIn Reply' },
  },
  {
    key: 'missed_reply', label: 'Missed reply', icon: 'MailQuestion', color: '#DC2626',
    labels: { tasks: 'Missed Reply', todayChip: 'Missed reply' },
  },
  {
    key: 'chatbot_lead', label: 'Chatbot lead', icon: 'MessageSquare', color: '#F97316',
    labels: { tasks: 'Chatbot Lead' },
    settingsColor: '#8b5cf6',
  },
  {
    key: 'meeting_booked', label: 'Meeting booked', icon: 'Calendar', color: '#EA580C',
    labels: { tasks: 'Meeting Booked', todayChip: 'Meeting booked' },
    settingsColor: '#22c55e',
  },
  {
    key: 'dialer_recording', label: 'Call recording', icon: 'Phone', color: '#06B6D4',
    labels: { tasks: 'Call Recording' },
  },
  {
    key: 'voice_recording', label: 'Meeting recording', icon: 'Phone', color: '#0891B2',
    labels: { tasks: 'Meeting Recording', todayChip: 'Meeting recording' },
    settingsColor: '#14b8a6',
  },
  // A tracked contact (champion) moving to a new company. The legacy generic
  // `job_change` type was retired (never produced; superseded by this one).
  {
    key: 'champion_job_change', label: 'Champion job change', icon: 'Briefcase', color: '#F43F5E',
    hub: { meaning: 'relationship' },
    labels: {
      tasks: 'Champion Job Change',
      taskChip: 'Past customer changed jobs',
      settings: 'Past customer changed jobs',
    },
  },
  // A buyer-persona person who just STARTED a role at an ICP account (Fiber people-search,
  // screened by Luna). The person is the contact, so it is relationship context, not a
  // company event like the posting-based `job_posting`.
  {
    key: 'new_hire', label: 'New hire', icon: 'UserPlus', color: '#0EA5E9',
    hub: { meaning: 'relationship', tooltip: 'A buyer-persona person just started a role at this account. They are the contact.' },
    labels: { tasks: 'New Hire', settings: 'New hire started a buyer role' },
  },
  {
    key: 'lookalike', label: 'Lookalike', icon: 'Users', color: '#8B5CF6',
    hub: { meaning: 'fit', tooltip: 'Fit recommendation. No activity observed yet.' },
    labels: { settings: 'Lookalike match' },
    settingsColor: '#a855f7',
  },
  {
    key: 'hubspot_stale', label: 'Stale lead', icon: 'Clock', color: '#94A3B8',
    labels: { tasks: 'Stale Lead' },
  },
  {
    key: 'custom_research', label: 'Research', icon: 'Search', color: '#34D399',
    labels: { taskChip: 'Custom signal' },
  },
  {
    // On the hub this is a person the rep saved from a LinkedIn profile with the extension's
    // Add to Signals (mb-frontend#4638); the rep enrolls them from the panel. Tasks keeps its
    // legacy wording for work created before the one-tap path.
    key: 'chrome_extension_task', label: 'Added from LinkedIn', icon: 'UserPlus',
    hub: { meaning: 'relationship', tooltip: 'Saved from a LinkedIn profile by a rep. Enroll to start outreach.' },
    labels: { tasks: 'Manual Task', settings: 'Chrome extension task' },
    settingsColor: '#0ea5e9',
  },
  // Manual promotion from the Signals dashboard.
  {
    key: 'manual_promotion', label: 'Manual promotion', icon: 'Zap', color: '#EF4444',
    labels: { tasks: 'Manual Promotion', taskChip: 'Manually promoted' },
    settingsColor: '#ec4899',
  },
  {
    key: 'community_mention', label: 'Community mention', icon: 'MessageCircle', color: '#F97316',
    hub: { meaning: 'activity' },
    labels: { tasks: 'Community Mention', taskChip: 'Social mention', settings: 'Social mention' },
  },
  {
    key: 'form_submission', label: 'Form submission', icon: 'FileText', color: '#84CC16',
    labels: { tasks: 'Form Submission', taskChip: 'Form submission' },
  },
  {
    key: 'task_follow_up', label: 'Follow-up', icon: 'RotateCcw', color: '#F43F5E',
    labels: { tasks: 'Follow-Up', settings: 'Follow-up task' },
    settingsColor: '#eab308',
  },
  // Signals Hub only: normalized types the unified signals endpoint emits that
  // never become a Tasks row of their own.
  { key: 'ad_engagement', label: 'Ad engagement', hub: { meaning: 'activity' } },
  {
    key: 'outreach_visit',
    label: 'Visited after outreach',
    hub: {
      meaning: 'activity',
      tooltip: 'Visited within 14 days after a MarketBetter email to this company. Matched by company and timing, not a tracked click.',
    },
  },
  {
    key: 'past_champion', label: 'Past champion',
    hub: { meaning: 'relationship', tooltip: 'Relationship context from a closed-won deal.' },
  },
  // A property of the account, not a signal: on the workspace's competitor list, or judged
  // one by Jev. mb-server accepts it as a filter-only key (HUB_FILTER_ONLY_SIGNAL_TYPES), never
  // as a catalogue type, so it never seeds rows or decides ownership.
  {
    key: 'competitor', label: 'Competitor',
    hub: { meaning: 'relationship', tooltip: 'On your competitor list, or identified by AI as a likely competitor.' },
  },
] as const satisfies readonly SignalTypeEntry[];

type RegistryEntry = typeof SIGNAL_TYPE_REGISTRY[number];

/** Every type Tasks can show (the keys of `SIGNAL_TYPE_CONFIG`). */
export type TaskSignalType = Extract<RegistryEntry, { icon: string }>['key'];

const REGISTRY_BY_KEY = new Map<string, SignalTypeEntry>(
  SIGNAL_TYPE_REGISTRY.map((entry) => [entry.key, entry]),
);

/** The registry entry for a key, if the frontend knows the type. */
export const signalTypeEntry = (key: string): SignalTypeEntry | undefined => REGISTRY_BY_KEY.get(key);

const SURFACE_FALLBACK: Record<SignalTypeSurface, (entry: SignalTypeEntry) => string> = {
  tasks: (entry) => entry.label,
  taskChip: (entry) => surfaceLabel(entry, 'tasks'),
  todayChip: (entry) => entry.shortLabel ?? surfaceLabel(entry, 'taskChip'),
  settings: (entry) => entry.label,
};

function surfaceLabel(entry: SignalTypeEntry, surface: SignalTypeSurface): string {
  return entry.labels?.[surface] ?? SURFACE_FALLBACK[surface](entry);
}

/**
 * How `surface` words a type it knows; `undefined` otherwise, so each surface
 * keeps its own fallback for custom agent types. The Tasks surfaces know only
 * Tasks types: a hub-only type reads there as the unknown type it always was.
 */
export const signalTypeLabel = (key: string, surface: SignalTypeSurface): string | undefined => {
  const entry = REGISTRY_BY_KEY.get(key);
  if (!entry || (surface !== 'settings' && !entry.icon)) return undefined;
  return surfaceLabel(entry, surface);
};

/** Hex colour per type for `colors.signals`; types without one fall back to its `default`. */
export const SIGNAL_TYPE_COLORS: Record<string, string> = Object.fromEntries(
  SIGNAL_TYPE_REGISTRY.flatMap((entry: SignalTypeEntry) => (entry.color ? [[entry.key, entry.color]] : [])),
);

/** The Tasks view of the registry (label + icon), in registry order. */
export const SIGNAL_TYPE_CONFIG: Record<string, { label: string; icon: string }> = Object.fromEntries(
  SIGNAL_TYPE_REGISTRY.flatMap((entry: SignalTypeEntry) => (
    entry.icon ? [[entry.key, { label: surfaceLabel(entry, 'tasks'), icon: entry.icon }]] : []
  )),
);

/** Settings view: wording and dot colour (lower-case hex, as settings has always used). */
export const settingsSignalTypeDisplay = (key: string): { label: string; color: string } | undefined => {
  const entry = REGISTRY_BY_KEY.get(key);
  if (!entry) return undefined;
  const color = entry.settingsColor ?? entry.color;
  return color ? { label: surfaceLabel(entry, 'settings'), color: color.toLowerCase() } : undefined;
};

/** Signals Hub filter order; the hub view below is built from it. */
const HUB_SIGNAL_TYPE_ORDER = [
  'website_visit',
  'vector_lead',
  'ad_engagement',
  'outreach_visit',
  'community_mention',
  'podcast_episode',
  'past_champion',
  'champion_job_change',
  'new_hire',
  'competitor',
  'job_posting',
  'funding_round',
  'market_intel',
  'competitor_event',
  'regulatory_filing',
  'dev_intent',
  'lookalike',
  'ai_discovery',
] as const satisfies readonly Extract<RegistryEntry, { hub: object }>['key'][];

export const SIGNAL_TYPE_CATALOG: SignalTypeDefinition[] = HUB_SIGNAL_TYPE_ORDER.map((key) => {
  const entry = REGISTRY_BY_KEY.get(key) as SignalTypeEntry & { hub: NonNullable<SignalTypeEntry['hub']> };
  return {
    key,
    label: entry.label,
    meaning: entry.hub.meaning,
    ...(entry.hub.tooltip ? { tooltip: entry.hub.tooltip } : {}),
  };
});

const BY_KEY = new Map(SIGNAL_TYPE_CATALOG.map((definition) => [definition.key, definition]));

export const SIGNAL_TYPE_KEYS = SIGNAL_TYPE_CATALOG.map((definition) => definition.key);

export const isCatalogSignalType = (value: string): boolean => BY_KEY.has(value);

/**
 * Definition for a type, or a neutral fallback so an unknown type coming from
 * the API still renders a readable chip instead of crashing the row.
 */
export const signalTypeDefinition = (key: string): SignalTypeDefinition => {
  const hubDefinition = BY_KEY.get(key);
  if (hubDefinition) return hubDefinition;
  // A registry type that is not a filterable hub type can still reach a row as a
  // chip — a LinkedIn capture (chrome_extension_task) seeds hub rows without being
  // a filter option — and it read "chrome extension task" instead of its label.
  const hub = REGISTRY_BY_KEY.get(key)?.hub;
  if (hub) {
    return {
      key,
      label: (REGISTRY_BY_KEY.get(key) as SignalTypeEntry).label,
      meaning: hub.meaning,
      ...(hub.tooltip ? { tooltip: hub.tooltip } : {}),
    };
  }
  return { key, label: key.replace(/_/g, ' '), meaning: 'activity' };
};

/**
 * Chip tone by meaning. Blue = observed activity, mint = relationship context,
 * amber = company event, slate = fit recommendation. These are Tailwind class
 * strings so the chip and the filter rows share one palette.
 */
export const SIGNAL_MEANING_TONE: Record<SignalMeaning, string> = {
  activity: 'border-[#D6E2FF] bg-[#EEF3FF] text-[#0639BA]',
  relationship: 'border-[#B8F3D8] bg-[#E3FBF1] text-[#1F6B4C]',
  company_event: 'border-amber-200 bg-amber-50 text-amber-800',
  fit: 'border-slate-200 bg-slate-100 text-slate-700',
};

/** Types whose recency is real. Fit-only rows sort after these and show an added date instead. */
export const isDatedSignalType = (key: string): boolean => {
  const meaning = signalTypeDefinition(key).meaning;
  return meaning !== 'fit' && key !== 'past_champion';
};
